// OLIVIUM · Alerts — real operator alert queue backed by olv_alerts.

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Banner } from '@square/ui'
import EmptyState from '../../components/EmptyState'
import { useOliviumAlerts } from '../../hooks/useOliviumAlerts'
import { useShell } from '../../shell/ShellContext'
import { resolveOliviumLinkedSurface } from './linkedSurface'

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime()
  if (isNaN(t)) return iso
  const diff = Math.max(0, Date.now() - t)
  const sec = Math.floor(diff / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  return `${Math.floor(hr / 24)}d ago`
}

function stringifyDetail(value: unknown): string | null {
  if (!value) return null
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return null
  }
}

function severityTone(severity: string) {
  switch (severity) {
    case 'critical':
    case 'alert':
      return {
        border: 'rgba(255,80,80,0.35)',
        bg: 'rgba(255,80,80,0.08)',
        text: '#ffbcbc',
      }
    case 'warning':
      return {
        border: 'rgba(255,196,77,0.35)',
        bg: 'rgba(255,196,77,0.08)',
        text: '#ffd898',
      }
    default:
      return {
        border: 'rgba(124,92,252,0.28)',
        bg: 'rgba(124,92,252,0.08)',
        text: '#d8cdfd',
      }
  }
}

export default function OliviumAlerts() {
  const navigate = useNavigate()
  const { openDetail } = useShell()
  const { alerts, loading, error, authRequired, updateAlert, refresh } = useOliviumAlerts()
  const [actionError, setActionError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const activeAlerts = useMemo(
    () => alerts.filter((alert) => alert.status !== 'resolved' && alert.status !== 'dismissed'),
    [alerts],
  )

  async function handleUpdate(id: string, status: 'acknowledged' | 'resolved') {
    setActionError(null)
    setBusyId(id)
    const updated = await updateAlert(id, status)
    if (!updated) setActionError(`Failed to mark alert ${status}.`)
    setBusyId(null)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div>
        <div
          style={{
            fontSize: 11,
            color: '#7f8db4',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.16em',
            marginBottom: 6,
          }}
        >
          OLIVIUM · ALERTS
        </div>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            margin: 0,
            color: '#edf2ff',
            fontFamily: 'var(--font-brand)',
          }}
        >
          Operator Alert Queue
        </h1>
        <div style={{ fontSize: 13, color: '#97a7cf', marginTop: 4 }}>
          Real alert rows from `olv_alerts` with acknowledge, resolve, and linked-surface actions.
        </div>
      </div>

      {error && (
        <Banner severity="critical" title="Alerts unavailable" onDismiss={() => void refresh()}>
          {error}
        </Banner>
      )}
      {actionError && (
        <Banner severity="warning" title="Alert action failed" onDismiss={() => setActionError(null)}>
          {actionError}
        </Banner>
      )}

      <section
        style={{
          border: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.035)',
          borderRadius: 18,
          overflow: 'hidden',
          padding: loading || authRequired || activeAlerts.length === 0 ? 24 : 16,
        }}
      >
        {authRequired ? (
          <EmptyState
            title="Sign in to view Alerts"
            body="OLIVIUM Alerts is backed by the live olv_alerts queue and requires an authenticated operator session."
          />
        ) : loading ? (
          <EmptyState title="Loading alerts" body="Reading olv_alerts." />
        ) : activeAlerts.length === 0 ? (
          <EmptyState title="No active alerts" body="Nothing in olv_alerts currently needs operator action." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activeAlerts.map((alert) => {
              const linked = resolveOliviumLinkedSurface(
                alert as unknown as Record<string, unknown>,
              )
              const tone = severityTone(alert.severity)
              const detail = stringifyDetail(alert.detail)
              return (
                <article
                  key={alert.id}
                  style={{
                    border: `1px solid ${tone.border}`,
                    background: tone.bg,
                    borderRadius: 12,
                    padding: 14,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: tone.text, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                        {alert.severity} · {alert.status}
                      </div>
                      <button
                        type="button"
                        onClick={() => openDetail(alert)}
                        style={{
                          padding: 0,
                          border: 'none',
                          background: 'transparent',
                          color: '#edf2ff',
                          fontSize: 15,
                          fontWeight: 700,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        {alert.title}
                      </button>
                    </div>
                    <div style={{ fontSize: 10, color: '#97a7cf', fontFamily: 'var(--font-mono)' }}>
                      {timeAgo(alert.created_at)}
                    </div>
                  </div>

                  {detail && (
                    <pre
                      style={{
                        margin: '10px 0 0',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: 11,
                        lineHeight: 1.5,
                        color: '#dbe5ff',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {detail}
                    </pre>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      flexWrap: 'wrap',
                      marginTop: 12,
                    }}
                  >
                    {alert.status === 'open' && (
                      <button
                        type="button"
                        onClick={() => void handleUpdate(alert.id, 'acknowledged')}
                        disabled={busyId === alert.id}
                        style={actionButton(tone.text, busyId === alert.id)}
                      >
                        {busyId === alert.id ? 'Working…' : 'Acknowledge'}
                      </button>
                    )}
                    {alert.status !== 'resolved' && (
                      <button
                        type="button"
                        onClick={() => void handleUpdate(alert.id, 'resolved')}
                        disabled={busyId === alert.id}
                        style={actionButton('#8ff0b3', busyId === alert.id)}
                      >
                        {busyId === alert.id ? 'Working…' : 'Resolve'}
                      </button>
                    )}
                    {linked && (
                      <button
                        type="button"
                        onClick={() => navigate(linked.path)}
                        style={actionButton('#d8cdfd', false)}
                      >
                        {linked.label}
                      </button>
                    )}
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}

function actionButton(color: string, disabled: boolean) {
  return {
    padding: '7px 12px',
    borderRadius: 999,
    border: `1px solid ${color}55`,
    background: 'transparent',
    color,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 10,
    fontFamily: 'var(--font-mono)',
    opacity: disabled ? 0.6 : 1,
  } as const
}
